import { Floor } from "@pdfking/math"
import { Range } from "@pdfking/tfunc"

import type { AgendaDesign, AgendaPageKind, PageContext } from "#internal"

import { AgendaFixedPages } from "./AgendaFixedPages"
import { AgendaWeeks } from "./AgendaWeeks"

/** What page `page_number` of an agenda of `design` for `year` shows: the
 * page a part of its own claims (see AgendaFixedPages), notes on any other
 * before the title; after it the year's days from the Monday of the week of
 * the 1st of January: a week a spread, its first `left` days on the left,
 * the rest on the right, or the week's notes there when the design has them;
 * a week a page; or a day a page, Saturday and Sunday sharing one. Any page
 * past them notes. */
export const AgendaPagePlan = (
  design: AgendaDesign,
  { page_number, year }: Pick<PageContext, "page_number" | "year">
): AgendaPageKind => {
  const fixed = AgendaFixedPages(design).find(([page]) => page === page_number)
  if (fixed) return fixed[1]
  if (page_number < design.title) return { enum: "notes" }
  const index = page_number - design.title - 1
  const { weeks } = AgendaWeeks(year)
  const per = { day: 6, week_page: 1, week_spread: 2 }[design.pages]
  const week = Floor(index / per)
  if (week >= weeks) return { enum: "notes" }
  if (design.pages === "week_page") return { enum: "week", week }
  if (design.pages === "day") {
    const slot = index % 6
    return { days: slot < 5 ? [slot] : [5, 6], enum: "day", week }
  }
  const days = [...Range(7)]
  if (!(index % 2)) return { days: days.slice(0, design.left), enum: "left", week }
  if (design.notes) return { enum: "week_notes", week }
  return { days: days.slice(design.left), enum: "right", week }
}
